import React from 'react';
import { Building2, Calendar, CheckCircle2, MessageCircle, ArrowRight, ShieldCheck, HeartHandshake, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const CorporateWellness = () => {
  const whatsappNumber = "+919088771406";

  const offerings = [
    {
      id: 'corporate-days',
      badge: 'Short-Term / Event Focus',
      title: 'Corporate Wellness Days',
      description: 'Customized wellness sessions designed for organizations that want a focused employee wellbeing experience over one or more days.',
      duration: '1 to 3 Days Event / Workshop',
      idealFor: 'Corporate retreats, wellness weeks, leadership alignment, and employee stress-relief events.',
      features: [
        'Employee wellness sessions',
        'Stress and burnout management',
        'Energy work & cleansing',
        'Group or mass healing sessions',
        'One-on-one wellness & healing sessions',
        'Leadership wellness & stress resilience',
        'Customized experiential wellness activities'
      ],
      message: 'Hi Lotus Prana, I would like to request a Corporate Proposal for Corporate Wellness Days.',
      accent: 'border-lotus-deep'
    },
    {
      id: '6-month-program',
      badge: 'Long-Term Partnership',
      title: '6-Month Customized Wellness Program',
      description: 'A long-term employee wellbeing partnership that provides customized wellness support throughout six months, with regular engagement and feedback.',
      duration: '6 Months Ongoing Partnership',
      idealFor: 'Organizations aiming for sustained culture transformation, reduced turnover, and boosted team morale.',
      features: [
        'Customized corporate wellness solutions',
        'Structured employee wellness programs',
        'Leadership wellness & executive coaching',
        'Mass or group healing sessions',
        'Targeted energy work for workplace harmony',
        'Stress & burnout management interventions',
        'One-on-one private wellness and healing sessions',
        'Monthly employee feedback & wellbeing reports',
        'Ongoing refinement of the program based on employee feedback'
      ],
      message: 'Hi Lotus Prana, I would like to request a Corporate Proposal for the 6-Month Customized Wellness Program.',
      accent: 'border-lotus-deep'
    }
  ];

  const getWaLink = (msg) => {
    return `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(msg)}`;
  };

  return (
    <section id="corporate-wellness" className="scroll-mt-24 bg-lotus-petal/20 px-6 py-20 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="mb-3 inline-block rounded-full bg-white px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-lotus-deep shadow-sm">
            Workplace Flourishing
          </span>
          <h2 className="font-serif text-3xl leading-tight text-lotus-ink sm:text-4xl lg:text-5xl">
            Corporate & Organizational Wellness
          </h2>
          <p className="mt-4 text-base text-lotus-ink/70 sm:text-lg">
            Empower your workplace with tailored energy and wellness solutions. Transform employee wellbeing, eliminate burnout, and foster sustainable organizational vitality.
          </p>
        </motion.div>

        {/* Corporate Grid */}
        <div className="mt-16 grid gap-10 lg:grid-cols-2">
          {offerings.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, delay: idx * 0.2 }}
              whileHover={{ y: -6 }}
              className="flex flex-col justify-between rounded-3xl bg-white p-8 sm:p-10 border border-lotus-petal shadow-md transition-all duration-300 hover:shadow-2xl hover:border-lotus-deep/60"
            >
              <div>
                {/* Header Badge */}
                <div className="flex items-center justify-between border-b border-lotus-petal/60 pb-4">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-lotus-deep">
                    <Building2 size={16} />
                    <span>{item.badge}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs font-medium text-lotus-ink/70">
                    <Calendar size={14} className="text-lotus-deep" />
                    <span>{item.duration}</span>
                  </div>
                </div>

                {/* Title & Description */}
                <h3 className="mt-6 font-serif text-2xl sm:text-3xl font-medium text-lotus-ink leading-snug">
                  {item.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-lotus-ink/75">
                  {item.description}
                </p>

                {/* Ideal For */}
                <div className="mt-6 rounded-2xl bg-background p-4 border border-lotus-petal/50">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-lotus-deep mb-1">
                    <HeartHandshake size={15} />
                    <span>Ideal For:</span>
                  </div>
                  <p className="text-xs sm:text-sm text-lotus-ink/80 leading-relaxed">
                    {item.idealFor}
                  </p>
                </div>

                {/* Features List */}
                <div className="mt-8 border-t border-lotus-petal/50 pt-6">
                  <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-lotus-ink/80 mb-4">
                    <ShieldCheck size={16} className="text-lotus-deep" />
                    Key Program Offerings
                  </h4>
                  <ul className="grid gap-3 sm:grid-cols-1 text-sm text-lotus-ink">
                    {item.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-3">
                        <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-lotus-deep" />
                        <span className="text-lotus-ink/90 font-normal leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Call To Action */}
              <div className="mt-10 pt-6 border-t border-lotus-petal/60">
                <motion.a
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.95 }}
                  href={getWaLink(item.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center gap-3 w-full rounded-full bg-lotus-deep py-4 px-6 text-sm font-semibold uppercase tracking-wider text-white transition-all duration-300 hover:bg-lotus-deep/90 shadow-md hover:shadow-lg active:scale-95"
                >
                  <MessageCircle size={20} className="transition-transform group-hover:scale-110" />
                  <span>Request a Corporate Proposal</span>
                  <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                </motion.a>
                <p className="mt-2.5 text-center text-xs text-lotus-ink/50">
                  Instant WhatsApp enquiry to discuss custom pricing & schedule
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Custom Corporate Note Banner */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 rounded-3xl bg-lotus-deep p-8 text-white shadow-xl lg:flex lg:items-center lg:justify-between lg:p-12"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/80 font-medium">
              <Users size={16} />
              <span>Tailored Organizational Consultation</span>
            </div>
            <h4 className="mt-2 font-serif text-2xl sm:text-3xl font-medium">
              Need a Customized Hybrid Event or Multi-Location Program?
            </h4>
            <p className="mt-3 text-sm sm:text-base text-white/90 leading-relaxed">
              We design bespoke energy wellness solutions adapted to your company size, work culture, and specific team dynamics.
            </p>
          </div>
          <div className="mt-6 lg:mt-0 shrink-0">
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href={getWaLink("Hi Lotus Prana, I would like to schedule a call to discuss a custom corporate wellness strategy for our organization.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-xs font-bold uppercase tracking-widest text-lotus-ink transition-all hover:bg-lotus-petal hover:shadow-lg active:scale-95"
            >
              <MessageCircle size={18} className="text-lotus-deep" />
              <span>Discuss Custom Strategy</span>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CorporateWellness;
